import * as Actions from '@/app/store/actions';
import * as PusherPushNotifications from '@pusher/push-notifications-web';
import * as UserActions from '@/app/auth/store/actions/user.actions';

import { getMessaging, getToken } from 'firebase/messaging';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

import { CircularProgress } from '@material-ui/core';
import CustomButton from '@/app/customComponents/CustomButton/CustomButton';
import DialogScrollContainer from '@shared/components/DialogScrollContainer';
import InfiniteScroll from 'react-infinite-scroll-component';
import JwtService from '@/app/services/jwtService';
import { app } from '@/utils/utils';
import axios from '@shared/axios-shared';
import { getMySchools } from '@/app/services/schools/schools';
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { sendFCMTokenToBackend } from '@/app/services/notifications/fcmServices';
import secureLocalStorage from 'react-secure-storage';
import { useNavigate } from 'react-router-dom';

const messaging = getMessaging(app);

const SchoolListDialog = () => {
	const dispatch = useDispatch();
	const user = useSelector(({ auth }) => auth.user);
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(false);
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(false);
	const [page, setPage] = useState(1);
	const [fetchingMore, setFetchingMore] = useState(false);
	const [rows, setRows] = useState([]);
	const [activeId, setActiveId] = useState(null);

	useEffect(() => {
		let isMounted = true;
		setIsLoading(true);
		getMySchools()
			.then((res) => {
				if (!isMounted) return;
				if (res.data.meta.last_page > res.data.meta.current_page) {
					setHasMore(true);
				} else {
					setHasMore(false);
				}
				setPage(res.data.meta.current_page + 1);
				setRows(res.data.data);
				setIsLoading(false);
			})
			.catch(() => {
				if (!isMounted) return;
				setIsLoading(false);
				dispatch(
					Actions.showMessage({
						message: 'Failed to fetch data, please refresh',
						variant: 'error',
					})
				);
			});

		return () => {
			isMounted = false;
		};
	}, []);

	const handleLoadMore = () => {
		setFetchingMore(true);
		getMySchools(null, page)
			.then((res) => {
				if (res.data.meta.last_page > res.data.meta.current_page) {
					setHasMore(true);
				} else {
					setHasMore(false);
				}
				setPage(res.data.meta.current_page + 1);
				setRows(rows.concat(res.data.data));
				setFetchingMore(false);
			})
			.catch(() => {
				setFetchingMore(false);
				dispatch(
					Actions.showMessage({
						message: 'Failed to fetch data, please refresh',
						variant: 'error',
					})
				);
			});
	};

	const handleSubmit = (school) => {
		if (!activeId) {
			return;
		}
		setLoading(true);
		if (school.otp) {
			if (['super_admin', 'company_owner', 'product_owner'].includes(user.role[0])) {
				secureLocalStorage.setItem('superadmin_token', JwtService.getAccessToken());
				secureLocalStorage.setItem(
					'superadmin_data',
					JSON.stringify({
						role: user.role[0],
						company_id:
							user.role[0] === 'product_owner' ? (user.data.selected_company ?? user.data.company_id ?? null) : null,
					})
				);
			}
			axios
				.post(`/api/v2/school/masquerade/${school.id}`, null, {
					headers: { Authorization: `Bearer ${secureLocalStorage.getItem('superadmin_token')}` },
				})
				.then((resp) => {
					secureLocalStorage.setItem('view_as_id', resp?.data?.user_id);
					window.navigator.serviceWorker.ready.then((serviceWorkerRegistration) => {
						const beamsClient = new PusherPushNotifications.Client({
							instanceId: import.meta.env.VITE_BEAMS_INSTANCE_ID,
							serviceWorkerRegistration,
						});
						beamsClient.clearAllState().then(() => console.log('Cleared all beams state'));
					});
					getToken(messaging, {
						vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
					})
						.then((deviceToken) => {
							if (deviceToken) {
								sendFCMTokenToBackend(deviceToken);
							} else {
								// Show permission request UI
								console.log('No registration token available. Request permission to generate one.');
								// ...
							}
						})
						.catch((err) => {
							console.log('An error occurred while retrieving token. ', err);
							// ...
						});
					user.data.selected_school = school.id;
					JwtService.setSession(resp.data.access_token);
					// axios.get('/api/v1/profile').then(res => {
					setLoading(false);
					JwtService.setViewAs(resp.data.access_token, school.id);
					dispatch(Actions.closeDialog());
					dispatch(
						UserActions.setUserData({
							...user,
							role: ['super_school_admin'],
							school,
						})
					);
					navigate('/');
				})
				.catch((err) => {
					dispatch(Actions.showMessage({ variant: 'error', message: err?.response?.data?.message }));
					dispatch(Actions.closeDialog());
				});
		}
	};

	return (
		<div className="bg-white px-32 min-h-[450px] max-h-[480px] pt-[10px] min-[400px]:min-w-[370px] min-[500px]:min-w-[440px]">
			<div className="flex justify-between py-[10px] px-0 content-center items-center mx-auto font-bold w-[90%]">
				<div>
					<h1 className="text-[20px] font-bold my-[13.4px]">Select School</h1>
				</div>
				<div>
					<i
						className="fas text-[20px]"
						onClick={() => dispatch(Actions.closeDialog())}
						style={{ cursor: 'pointer' }}
					/>
				</div>
			</div>

			<DialogScrollContainer id="scrollable-list">
				{isLoading ? (
					<div className="w-1/12 mx-auto mt-16">
						<CircularProgress size={35} />
					</div>
				) : (
					rows?.map((school, i, arr) => {
						return (
							<div
								key={school.id}
								onClick={() => setActiveId(school.id)}
								style={{
									cursor: 'pointer',
									background: school.id === activeId ? '#F0F9FF' : 'white',
								}}
							>
								<div className="p-16 flex justify-between">
									<div className="w-[310px]">
										<h5 className="text-[14px]">{school.name}</h5>
									</div>
									<div
										className="rounded-full flex justify-center items-center p-4 text-white border-[1px] border-[#4DA0EE] w-[24px] h-[24px]"
										style={{ background: school.id === activeId ? '#4DA0EE' : 'white' }}
									>
										<i className="fas fa-check" />
									</div>
								</div>
								{i !== arr.length - 1 ? <hr style={{ borderColor: 'lightgray' }} /> : null}
							</div>
						);
					})
				)}
				{fetchingMore ? (
					<div className="w-1/12 mx-auto">
						<CircularProgress size={35} />
					</div>
				) : null}
			</DialogScrollContainer>
			<div className="w-full flex justify-center">
				{loading ? (
					<div className="w-1/12 mx-auto mt-24">
						<CircularProgress size={35} />
					</div>
				) : (
					<div className="mt-[25px]">
						<CustomButton
							height={38}
							onClick={() => handleSubmit(rows.filter((row) => row.id === activeId)[0])}
							type="button"
							width={156}
						>
							View School
						</CustomButton>
					</div>
				)}
			</div>
			<InfiniteScroll
				dataLength={rows.length}
				hasMore={hasMore}
				next={handleLoadMore}
				scrollableTarget="scrollable-list"
			/>
		</div>
	);
};

export default SchoolListDialog;
